<script setup>
import { computed, onBeforeMount, watch, ref } from 'vue';
import {
  useStoreGetters,
  useStore,
  useFunctionGetter,
} from 'dashboard/composables/store';
import { isEmptyObject } from 'widget/helpers/utils';
import { useCamelCase } from 'dashboard/composables/useTransformKeys';

import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ContactReasonTree from './ContactReasonTree.vue';
import AddPopup from './AddPopup.vue';
import ContactReasonDetails from './ContactReasonDetails.vue';

const store = useStore();
const getters = useStoreGetters();

const showAddPopup = ref(false);
const selectedContactReason = ref(null);
const selectedContactReasonPath = ref([]);
const storedContactReason = useFunctionGetter(
  'contactReasons/getContactReasonByPath',
  selectedContactReasonPath
);
const contactReasons = computed(() =>
  useCamelCase(getters['contactReasons/getContactReasons'].value, {
    deep: true,
  })
);
const uiFlags = computed(() => getters['contactReasons/getUIFlags'].value);

const openAddPopup = () => {
  showAddPopup.value = true;
};

const hideAddPopup = () => {
  showAddPopup.value = false;
};

const onSelectContactReason = contactReasonObj => {
  selectedContactReason.value = contactReasonObj.reason;
  selectedContactReasonPath.value = contactReasonObj.path;
};

const onCreateContactReason = newContactReason => {
  selectedContactReason.value = useCamelCase(newContactReason, { deep: true });
};

const onUpdatedContactReason = updatedContactReason => {
  selectedContactReason.value = useCamelCase(updatedContactReason, {
    deep: true,
  });
};

onBeforeMount(() => {
  store.dispatch('contactReasons/get');
});

watch(
  contactReasons,
  list => {
    if (list.length > 0 && !selectedContactReason.value) {
      selectedContactReason.value = list[0];
      selectedContactReasonPath.value = [list[0]?.id];
    } else if (list.length === 0 || isEmptyObject(storedContactReason.value)) {
      selectedContactReason.value = null;
      selectedContactReasonPath.value = [];
    }
  },
  { immediate: true }
);
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('CONTACT_REASONS.LOADING')"
    :no-records-found="!contactReasons.length"
    :no-records-message="$t('CONTACT_REASONS.NO_RECORDS_MESSAGE')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('CONTACT_REASONS.HEADER')"
        :description="$t('CONTACT_REASONS.DESCRIPTION')"
      >
        <template #actions>
          <Button
            icon="i-lucide-circle-plus"
            :label="$t('CONTACT_REASONS.HEADER_BTN_TXT')"
            @click="openAddPopup"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <div class="flex gap-4">
        <div class="flex-1">
          <ContactReasonTree
            :contact-reasons="contactReasons"
            :selected-id="selectedContactReason?.id"
            root-node
            @select-contact-reason="onSelectContactReason"
            @created="onCreateContactReason"
          />
        </div>
        <div class="flex-1 sticky top-12 self-start">
          <ContactReasonDetails
            :contact-reason="selectedContactReason"
            :contact-reason-path="selectedContactReasonPath"
            @updated="onUpdatedContactReason"
          />
        </div>
      </div>
    </template>
    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <AddPopup
        :contact-reason="selectedContactReason"
        @close="hideAddPopup"
        @created="onCreateContactReason"
      />
    </woot-modal>
  </SettingsLayout>
</template>
